/**
 * WB4 Wave 2 — Deferred high-tier action (delete blocked until correction approved).
 */

import type { ObjectRef } from '@shared/business-workbench/workbench-focus';

export const WORKBENCH_DEFERRED_HIGH_TIER_EXECUTE_EVENT =
  'inflomatrix:workbench-deferred-high-tier-execute';

export interface WorkbenchDeferredHighTierActionEntry {
  tenantId: string;
  objectRef: ObjectRef;
  handlerRef: string;
  actionId: string;
  actionLabel: string;
  recordedAt: string;
}

export interface WorkbenchDeferredHighTierExecuteDetail {
  tenantId: string;
  objectRef: ObjectRef;
  handlerRef: string;
  actionId: string;
  actionLabel: string;
}

const deferredByObjectKey = new Map<string, WorkbenchDeferredHighTierActionEntry>();

function scopeKey(tenantId: string, objectId: string): string {
  return `${tenantId}:${objectId}`;
}

export function markDeferredHighTierAction(
  input: Omit<WorkbenchDeferredHighTierActionEntry, 'recordedAt'> & { recordedAt?: string }
): WorkbenchDeferredHighTierActionEntry {
  const entry: WorkbenchDeferredHighTierActionEntry = {
    ...input,
    recordedAt: input.recordedAt ?? new Date().toISOString(),
  };
  deferredByObjectKey.set(scopeKey(input.tenantId, input.objectRef.id), entry);
  return entry;
}

export function getDeferredHighTierAction(
  tenantId: string,
  objectId: string
): WorkbenchDeferredHighTierActionEntry | null {
  return deferredByObjectKey.get(scopeKey(tenantId, objectId)) ?? null;
}

export function clearDeferredHighTierAction(tenantId: string, objectId: string): boolean {
  return deferredByObjectKey.delete(scopeKey(tenantId, objectId));
}

export function dispatchDeferredHighTierExecute(
  detail: WorkbenchDeferredHighTierExecuteDetail
): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(
    new CustomEvent<WorkbenchDeferredHighTierExecuteDetail>(
      WORKBENCH_DEFERRED_HIGH_TIER_EXECUTE_EVENT,
      {
        detail,
      }
    )
  );
}

export function executeDeferredHighTierActionIfApproved(input: {
  tenantId: string;
  objectRef: ObjectRef;
  handlerRef: string;
  actionId: string;
  actionLabel: string;
}): boolean {
  const deferred = getDeferredHighTierAction(input.tenantId, input.objectRef.id);
  if (!deferred || deferred.handlerRef !== input.handlerRef) {
    return false;
  }
  clearDeferredHighTierAction(input.tenantId, input.objectRef.id);
  dispatchDeferredHighTierExecute({
    tenantId: input.tenantId,
    objectRef: input.objectRef,
    handlerRef: input.handlerRef,
    actionId: input.actionId,
    actionLabel: input.actionLabel,
  });
  return true;
}

/** @internal test-only */
export function resetDeferredHighTierActionForTests(): void {
  deferredByObjectKey.clear();
}
